'use client';

import React from 'react';
import toast from 'react-hot-toast';
import type {
  PlatformMessageRow,
  PlatformMessagePosition,
  PlatformMessageRenderStyle,
} from './api';

/**
 * Dispara um toast respeitando a configuracao de renderizacao salva no
 * banco (renderStyle, position, durationMs). Quando algum campo eh null,
 * usa o padrao global do Toaster (definido em app/layout.tsx).
 *
 * Estilos suportados:
 *   - 'default' / null      = toast.success/error/blank baseado no toneFromKey
 *   - 'bootstrap-basic'     = header colorido + corpo + close
 *   - 'bootstrap-compact'   = sem header, corpo + close inline
 *   - 'bootstrap-action'    = corpo + linha de botoes (sticky)
 *   - 'rich-avatar'         = avatar + titulo + linha secundaria
 *   - 'sticky'              = duracao infinita com botao de fechar
 *
 * `vars` interpola placeholders {name} no value antes de renderizar.
 */
export interface FireToastFromMessageOptions {
  vars?: Record<string, string | number | undefined>;
  onAction?: () => void;
}

type ToastTone = 'success' | 'error' | 'warning' | 'info';

function toneFromKey(key: string): ToastTone {
  if (key.startsWith('toast.success')) return 'success';
  if (key.startsWith('toast.error')) return 'error';
  if (key.startsWith('toast.warning')) return 'warning';
  return 'info';
}

function interpolate(
  template: string,
  vars: Record<string, string | number | undefined>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, name) => {
    const v = vars[name];
    if (v === undefined || v === null) return `{${name}}`;
    return String(v);
  });
}

function effectiveDuration(durationMs: number | null): number | undefined {
  if (durationMs === null) return undefined; // usa default global
  if (durationMs <= 0) return Infinity; // sticky
  return durationMs;
}

function toneIconClass(tone: ToastTone): string {
  if (tone === 'success') return 'bs-toast__icon-block--success';
  if (tone === 'error') return 'bs-toast__icon-block--error';
  if (tone === 'warning') return 'bs-toast__icon-block--warning';
  return '';
}

export function fireToastFromMessage(
  row: Pick<
    PlatformMessageRow,
    'key' | 'value' | 'renderStyle' | 'position' | 'durationMs'
  > & { description?: string | null },
  options: FireToastFromMessageOptions = {},
): string {
  const text = interpolate(row.value, options.vars ?? {});
  const tone = toneFromKey(row.key);
  const duration = effectiveDuration(row.durationMs);
  const position = (row.position ?? undefined) as
    | PlatformMessagePosition
    | undefined;
  const style: PlatformMessageRenderStyle = row.renderStyle ?? 'default';

  const baseOpts: { duration?: number; position?: PlatformMessagePosition } = {};
  if (duration !== undefined) baseOpts.duration = duration;
  if (position !== undefined) baseOpts.position = position;

  if (style === 'default') {
    if (tone === 'success') return toast.success(text, baseOpts);
    if (tone === 'error') return toast.error(text, baseOpts);
    if (tone === 'warning') return toast(text, { ...baseOpts, icon: '⚠️' });
    return toast(text, { ...baseOpts, icon: 'ℹ️' });
  }

  if (style === 'bootstrap-basic') {
    return toast.custom(
      (t) => (
        <div className={`bs-toast ${t.visible ? 'bs-toast--show' : 'bs-toast--hide'}`}>
          <div className="bs-toast__header">
            <span
              className={`bs-toast__icon-block ${toneIconClass(tone)}`}
              aria-hidden="true"
            />
            <strong className="bs-toast__title">7barber</strong>
            <span className="bs-toast__time">agora</span>
            <button
              type="button"
              className="bs-toast__close"
              onClick={() => toast.dismiss(t.id)}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
          <div className="bs-toast__body">{text}</div>
        </div>
      ),
      baseOpts,
    );
  }

  if (style === 'bootstrap-compact') {
    return toast.custom(
      (t) => (
        <div className={`bs-toast ${t.visible ? 'bs-toast--show' : 'bs-toast--hide'}`}>
          <div className="bs-toast__body bs-toast__body--compact">
            <p>{text}</p>
            <button
              type="button"
              className="bs-toast__close"
              onClick={() => toast.dismiss(t.id)}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>
      ),
      baseOpts,
    );
  }

  if (style === 'bootstrap-action') {
    return toast.custom(
      (t) => (
        <div className={`bs-toast ${t.visible ? 'bs-toast--show' : 'bs-toast--hide'}`}>
          <div className="bs-toast__body">{text}</div>
          <div className="bs-toast__actions-row">
            <button
              type="button"
              className="btn btn--admin btn--sm"
              onClick={() => {
                toast.dismiss(t.id);
                options.onAction?.();
              }}
            >
              Confirmar
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => toast.dismiss(t.id)}
            >
              Fechar
            </button>
          </div>
        </div>
      ),
      { ...baseOpts, duration: baseOpts.duration ?? Infinity },
    );
  }

  if (style === 'rich-avatar') {
    return toast.custom(
      (t) => (
        <div
          className={`bs-toast bs-toast--rich ${t.visible ? 'bs-toast--show' : 'bs-toast--hide'}`}
        >
          <div className="bs-toast__avatar" aria-hidden="true">
            !
          </div>
          <div className="bs-toast__rich-body">
            <p className="bs-toast__rich-title">{row.description ?? '7barber'}</p>
            <p className="bs-toast__rich-msg">{text}</p>
          </div>
          <button
            type="button"
            className="bs-toast__close"
            onClick={() => toast.dismiss(t.id)}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
      ),
      baseOpts,
    );
  }

  // style === 'sticky'
  return toast.custom(
    (t) => (
      <div className={`bs-toast ${t.visible ? 'bs-toast--show' : 'bs-toast--hide'}`}>
        <div className="bs-toast__header">
          <span
            className={`bs-toast__icon-block ${toneIconClass(tone)}`}
            aria-hidden="true"
          />
          <strong className="bs-toast__title">Atenção</strong>
          <span className="bs-toast__time">persistente</span>
          <button
            type="button"
            className="bs-toast__close"
            onClick={() => toast.dismiss(t.id)}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
        <div className="bs-toast__body">{text}</div>
      </div>
    ),
    { ...baseOpts, duration: Infinity },
  );
}
